import DOMPurify from "dompurify";
import { ExternalService } from "../../services/ExternalService";
import { Files } from "../../services/FilesService";
import { ReflectionService } from "../../services/ReflectionService";
import generateUuid from "../../utils/Uuid";

export async function fetchHelperTextForTemplate(templateSections) {
  const promiseList = templateSections.map((section) =>
    Files.getFileContent("response.html", "templateHelperText", section.id)
  );
  const helperTexts = await Promise.all(promiseList);
  return helperTexts;
}

export async function upsertReflection({
  reflectionText,
  author,
  contextId,
  reflectionId = null,
  authorId = null,
  actions = [],
  externalUserName = null,
}) {
  const sanitizedReflection = DOMPurify.sanitize(reflectionText);

  const upsertReflection = externalUserName
    ? ExternalService.addExternalReflectionForContext
    : ReflectionService.addReflectionForContext;
  const id = reflectionId ?? generateUuid();

  // POST HTML content directly to backend endpoint for sanitization
  await upsertReflection({
    reflectionId: id,
    contextId: contextId,
    author: author,
    actions: actions,
    userId: authorId,
    htmlContent: sanitizedReflection,
  });

  const newReflection = {
    ddl_id: id,
    content: sanitizedReflection,
    is_draft: false,
    metadata: { author: author },
    actions: actions,
    timestamp: new Date().toISOString(),
  };

  return newReflection;
}

export async function upsertReflectionFromTemplate({
  sections,
  author,
  contextId,
  templateId,
  reflectionId = null,
  authorId = null,
  actions = [],
  externalUserName = false,
}) {
  const upsertReflection = externalUserName
    ? ExternalService.addExternalReflectionForContext
    : ReflectionService.addReflectionForContext;
  const id = reflectionId ?? generateUuid();

  // Upload each section's HTML content directly to backend
  const uploadPromises = sections.map((section) => {
    const sanitizedReflection = DOMPurify.sanitize(section.content);
    return upsertReflection({
      reflectionId: id,
      sectionId: section.id,
      templateId: templateId,
      contextId: contextId,
      author: author,
      actions: actions,
      userId: authorId,
      htmlContent: sanitizedReflection,
    });
  });

  await Promise.all(uploadPromises);

  const newReflection = {
    ddl_id: id,
    metadata: { author: author },
    actions: [],
    timestamp: new Date().toISOString(),
    sections: sections.map((section) => ({
      id: section.id,
      content: DOMPurify.sanitize(section.content),
      header_question: section.header_question,
      helper_text: section.helper_text,
    })),
    from_template: true,
    template_id: templateId,
  };

  return newReflection;
}

export async function upsertReflectionDraft({
  reflectionText,
  author,
  authorId,
  contextId,
  reflectionId = null,
}) {
  const sanitizedReflection = DOMPurify.sanitize(reflectionText);

  const id = reflectionId ?? generateUuid();

  // POST HTML content directly to backend endpoint for sanitization
  await ReflectionService.createReflectionDraft({
    reflectionId: id,
    contextId: contextId,
    author: author,
    userId: authorId,
    htmlContent: sanitizedReflection,
  });

  const newReflection = {
    ddl_id: id,
    content: sanitizedReflection,
    metadata: { author: author },
    is_draft: true,
    actions: [],
    timestamp: new Date().toISOString(),
    name: "response.html",
  };

  return newReflection;
}

export async function upsertReflectionDraftFromTemplate({
  sections,
  author,
  authorId,
  contextId,
  templateId,
  reflectionId,
}) {
  const id = reflectionId ?? generateUuid();

  // Upload each section's HTML content directly to backend
  const uploadPromises = sections.map((refSection) => {
    const sanitizedReflection = DOMPurify.sanitize(refSection.content);
    return ReflectionService.createReflectionDraft({
      reflectionId: id,
      sectionId: refSection.id,
      templateId: templateId,
      contextId: contextId,
      author: author,
      userId: authorId,
      htmlContent: sanitizedReflection,
    });
  });

  await Promise.all(uploadPromises);

  const newReflection = {
    ddl_id: id,
    metadata: { author: author },
    actions: [],
    timestamp: new Date().toISOString(),
    sections: sections.map((section) => ({
      id: section.id,
      content: DOMPurify.sanitize(section.content),
      header_question: section.header_question,
      helper_text: section.helper_text,
    })),
    from_template: true,
    template_id: templateId,
    is_draft: true,
  };

  return newReflection;
}
